import { Header, notify } from "@general-backoffice/core";
import type { EmailTemplate } from "@investhinks/api-client";
import classnames from "classnames";
import React, { type ChangeEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import { emailTemplatesEndpoints } from "../../../../api";

interface TemplateForm {
  subject: string;
  title: string;
  titleColor: string;
  greeting: string;
  bodyParagraphs: string[];
  buttonText: string;
  buttonUrl: string;
  closingText: string;
}

interface EditForms {
  es: TemplateForm;
  en: TemplateForm;
}

interface UnsavedChanges {
  es: boolean;
  en: boolean;
}

type LanguageCode = "es" | "en";

// Templates grouped by trigger and language
type TemplatesMap = Record<string, Record<string, EmailTemplate>>;

const TITLE_COLORS = [
  { value: "default", color: "#343a40" },
  { value: "success", color: "#59c596" },
  { value: "danger", color: "#ff513a" },
  { value: "warning", color: "#ffc107" },
  { value: "info", color: "#3aa1ff" },
];

const TRIGGER_KEYS = [
  "welcome",
  "email_confirmation",
  "manual_payment_invoice",
  "payment_confirmation",
  "payout_success",
  "charge_success",
  "subscription_failure",
  "subscription_failure_escalated",
  "bot_deactivation",
  "partner_warning",
  "partner_blocked",
  "drawdown_20",
  "newsletter",
];

const getEmptyForm = (): TemplateForm => ({
  subject: "",
  title: "",
  titleColor: "default",
  greeting: "",
  bodyParagraphs: [""],
  buttonText: "",
  buttonUrl: "",
  closingText: "",
});

const templateToForm = (template: EmailTemplate | undefined): TemplateForm => ({
  subject: template?.subject || "",
  title: template?.title || "",
  titleColor: template?.titleColor || "default",
  greeting: template?.greeting || "",
  bodyParagraphs:
    Array.isArray(template?.bodyParagraphs) && template.bodyParagraphs.length > 0 ? template.bodyParagraphs : [""],
  buttonText: template?.buttonText || "",
  buttonUrl: template?.buttonUrl || "",
  closingText: template?.closingText || "",
});

const EmailTemplates: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "emailTemplates" });

  // State
  const [templates, setTemplates] = useState<TemplatesMap>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selected trigger
  const [selectedTrigger, setSelectedTrigger] = useState<string | null>(null);
  const [activeLanguage, setActiveLanguage] = useState<LanguageCode>("es");

  // Edit modal state - forms for both languages
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForms, setEditForms] = useState<EditForms>({ es: getEmptyForm(), en: getEmptyForm() });
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<UnsavedChanges>({ es: false, en: false });

  // Preview modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Test email modal state
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testEmail, setTestEmail] = useState(() => localStorage.getItem("emailTemplates_testEmail") || "");
  const [isSendingTest, setIsSendingTest] = useState(false);

  // Variables for current trigger
  const [triggerVariables, setTriggerVariables] = useState<string[]>([]);

  // Toggle active state
  const [isTogglingActive, setIsTogglingActive] = useState<string | null>(null);

  // Helper to check if a trigger is active
  const isTriggerActive = (trigger: string): boolean => {
    const esTemplate = templates[trigger]?.es;
    const enTemplate = templates[trigger]?.en;
    if (esTemplate && enTemplate) {
      return esTemplate.isActive !== false || enTemplate.isActive !== false;
    }
    return esTemplate?.isActive !== false && enTemplate?.isActive !== false;
  };

  // Helper to check if templates exist
  const hasTemplates = (trigger: string): boolean => {
    return !!(templates[trigger]?.es || templates[trigger]?.en);
  };

  const handleToggleActive = async (trigger: string) => {
    const esTemplate = templates[trigger]?.es;
    const enTemplate = templates[trigger]?.en;
    const templateIds = [esTemplate?.id, enTemplate?.id].filter((id): id is number => id !== undefined);

    if (templateIds.length === 0) return;

    const currentlyActive = isTriggerActive(trigger);
    const newActiveState = !currentlyActive;

    try {
      setIsTogglingActive(trigger);
      await Promise.all(templateIds.map((id) => emailTemplatesEndpoints.toggleActive(id, newActiveState)));
      notify.success(String(newActiveState ? t("triggerEnabled") : t("triggerDisabled")));
      await loadTemplates();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(t("toggleError"));
      notify.error(errorMessage);
    } finally {
      setIsTogglingActive(null);
    }
  };

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await emailTemplatesEndpoints.getList();

      const grouped: TemplatesMap = {};
      if (response?.data) {
        Object.entries(response.data).forEach(([trigger, templateList]) => {
          if (Array.isArray(templateList)) {
            grouped[trigger] = {};
            templateList.forEach((template) => {
              if (template.language) {
                grouped[trigger][template.language] = template;
              }
            });
          }
        });
      }

      setTemplates(grouped);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load templates";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const loadVariables = async (trigger: string) => {
    try {
      const response = await emailTemplatesEndpoints.getVariables(trigger);
      setTriggerVariables(response.data.variables);
    } catch (err: unknown) {
      console.error("Failed to load variables:", err);
      setTriggerVariables([]);
    }
  };

  const openEditModal = (trigger: string) => {
    const esTemplate = templates[trigger]?.es;
    const enTemplate = templates[trigger]?.en;

    setEditForms({
      es: templateToForm(esTemplate),
      en: templateToForm(enTemplate),
    });
    setHasUnsavedChanges({ es: false, en: false });
    setSelectedTrigger(trigger);
    setActiveLanguage("es");
    loadVariables(trigger);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    const hasChanges = hasUnsavedChanges.es || hasUnsavedChanges.en;
    if (hasChanges) {
      if (!window.confirm(t("unsavedChangesConfirm"))) {
        return;
      }
    }
    setEditModalOpen(false);
    setSelectedTrigger(null);
    setEditForms({ es: getEmptyForm(), en: getEmptyForm() });
    setHasUnsavedChanges({ es: false, en: false });
    setTriggerVariables([]);
  };

  const handleLanguageChange = (lang: LanguageCode) => {
    setActiveLanguage(lang);
  };

  const handleFormChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setEditForms((prev) => ({
        ...prev,
        [activeLanguage]: {
          ...prev[activeLanguage],
          [name]: value,
        },
      }));
      setHasUnsavedChanges((prev) => ({ ...prev, [activeLanguage]: true }));
    },
    [activeLanguage]
  );

  const handleParagraphChange = (index: number, value: string) => {
    setEditForms((prev) => {
      const newParagraphs = [...prev[activeLanguage].bodyParagraphs];
      newParagraphs[index] = value;
      return {
        ...prev,
        [activeLanguage]: {
          ...prev[activeLanguage],
          bodyParagraphs: newParagraphs,
        },
      };
    });
    setHasUnsavedChanges((prev) => ({ ...prev, [activeLanguage]: true }));
  };

  const addParagraph = () => {
    setEditForms((prev) => ({
      ...prev,
      [activeLanguage]: {
        ...prev[activeLanguage],
        bodyParagraphs: [...prev[activeLanguage].bodyParagraphs, ""],
      },
    }));
    setHasUnsavedChanges((prev) => ({ ...prev, [activeLanguage]: true }));
  };

  const removeParagraph = (index: number) => {
    setEditForms((prev) => ({
      ...prev,
      [activeLanguage]: {
        ...prev[activeLanguage],
        bodyParagraphs: prev[activeLanguage].bodyParagraphs.filter((_, i) => i !== index),
      },
    }));
    setHasUnsavedChanges((prev) => ({ ...prev, [activeLanguage]: true }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Save both languages if they have changes
      const savePromises: Promise<unknown>[] = [];

      const langs: LanguageCode[] = ["es", "en"];
      for (const lang of langs) {
        if (hasUnsavedChanges[lang] && selectedTrigger) {
          const template = templates[selectedTrigger]?.[lang];
          if (template) {
            const form = editForms[lang];
            const data = {
              subject: form.subject,
              title: form.title,
              titleColor: form.titleColor,
              greeting: form.greeting || null,
              bodyParagraphs: form.bodyParagraphs.filter((p) => p.trim() !== ""),
              buttonText: form.buttonText || null,
              buttonUrl: form.buttonUrl || null,
              closingText: form.closingText || null,
            };
            savePromises.push(emailTemplatesEndpoints.update(template.id, data));
          }
        }
      }

      if (savePromises.length > 0) {
        await Promise.all(savePromises);
        notify.success(t("saveSuccess"));
        setHasUnsavedChanges({ es: false, en: false });
        await loadTemplates();
      } else {
        notify.info(t("noChangesToSave"));
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(t("saveError"));
      notify.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!selectedTrigger) return;
    const template = templates[selectedTrigger]?.[activeLanguage];
    if (!template) return;

    try {
      setIsLoadingPreview(true);
      const response = await emailTemplatesEndpoints.preview(template.id, activeLanguage);
      setPreviewHtml(response.data.html);
      setPreviewModalOpen(true);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to generate preview";
      notify.error(errorMessage);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const openTestModal = () => {
    setTestModalOpen(true);
  };

  const handleSendTest = async () => {
    if (!selectedTrigger) return;
    const template = templates[selectedTrigger]?.[activeLanguage];
    if (!template || !testEmail) return;

    try {
      setIsSendingTest(true);
      localStorage.setItem("emailTemplates_testEmail", testEmail);
      await emailTemplatesEndpoints.sendTest(template.id, testEmail);
      notify.success(t("testEmailSent"));
      setTestModalOpen(false);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to send test email";
      notify.error(errorMessage);
    } finally {
      setIsSendingTest(false);
    }
  };

  const triggers = TRIGGER_KEYS;
  const currentForm = editForms[activeLanguage];
  const currentTemplate = selectedTrigger ? templates[selectedTrigger]?.[activeLanguage] : undefined;

  return (
    <React.Fragment>
      <Header.BreadCrumb color="secondary" isDark={false}>
        <Col xs={12}>
          <h2 className="text-white mb-0">{t("title") || "Email Templates"}</h2>
        </Col>
      </Header.BreadCrumb>

      <Container className="mt--6" fluid>
        {error && (
          <Alert color="danger" className="mb-4">
            {error}
            <button type="button" className="close" onClick={() => setError(null)}>
              <span>&times;</span>
            </button>
          </Alert>
        )}

        <Row>
          <Col>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">{t("templatesList") || "Email Templates"}</h3>
                <Button color="secondary" size="sm" onClick={loadTemplates} disabled={isLoading}>
                  <i className="fas fa-sync-alt mr-2"></i>
                  {t("refresh") || "Refresh"}
                </Button>
              </CardHeader>
              <CardBody>
                {isLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-3">{t("loading") || "Loading templates..."}</p>
                  </div>
                ) : (
                  <Table responsive hover>
                    <thead className="thead-light">
                      <tr>
                        <th>{t("trigger") || "Trigger"}</th>
                        <th className="text-center" style={{ width: "120px" }}>
                          {t("status") || "Status"}
                        </th>
                        <th className="text-center" style={{ width: "100px" }}>
                          {t("actions") || "Actions"}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {triggers.map((trigger) => {
                        const esTemplate = templates[trigger]?.es;
                        const enTemplate = templates[trigger]?.en;
                        const isActive = isTriggerActive(trigger);
                        const hasTpl = hasTemplates(trigger);
                        const missingLanguages = [];
                        if (!esTemplate) missingLanguages.push("ES");
                        if (!enTemplate) missingLanguages.push("EN");

                        return (
                          <tr key={trigger} className={!isActive ? "table-secondary" : ""}>
                            <td>
                              <strong>{t(`triggers.${trigger}`)}</strong>
                              {missingLanguages.length > 0 && (
                                <Badge
                                  color="warning"
                                  className="ml-2"
                                  title={`Missing: ${missingLanguages.join(", ")}`}
                                >
                                  <i className="fas fa-exclamation-triangle mr-1"></i>
                                  {missingLanguages.join(", ")}
                                </Badge>
                              )}
                              <br />
                              <code className="text-muted small">{trigger}</code>
                            </td>
                            <td className="text-center">
                              {hasTpl ? (
                                <Button
                                  color={isActive ? "success" : "secondary"}
                                  size="sm"
                                  onClick={() => handleToggleActive(trigger)}
                                  disabled={isTogglingActive === trigger}
                                  style={{ minWidth: "90px" }}
                                >
                                  {isTogglingActive === trigger ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    <>
                                      <i className={`fas fa-${isActive ? "check" : "times"} mr-1`}></i>
                                      {isActive ? t("enabled") || "On" : t("disabled") || "Off"}
                                    </>
                                  )}
                                </Button>
                              ) : (
                                <Badge color="light" className="text-muted">
                                  -
                                </Badge>
                              )}
                            </td>
                            <td className="text-center">
                              {hasTpl ? (
                                <Button color="primary" size="sm" onClick={() => openEditModal(trigger)}>
                                  <i className="fas fa-edit"></i>
                                </Button>
                              ) : (
                                <Badge color="secondary">{t("notConfigured") || "N/A"}</Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Info Card */}
        <Row className="mt-4">
          <Col md={6}>
            <Card>
              <CardHeader>
                <h4 className="mb-0">
                  <i className="fas fa-info-circle text-info mr-2"></i>
                  {t("howItWorks") || "How it works"}
                </h4>
              </CardHeader>
              <CardBody>
                <ul className="mb-0">
                  <li>{t("info1") || "Edit the text content of each email template"}</li>
                  <li>{t("info2") || "Use {{variableName}} to insert dynamic values"}</li>
                  <li>{t("info3") || "Preview your changes before saving"}</li>
                  <li>{t("info4") || "Send a test email to verify the result"}</li>
                </ul>
              </CardBody>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <CardHeader>
                <h4 className="mb-0">
                  <i className="fas fa-globe text-primary mr-2"></i>
                  {t("languageSupport") || "Language Support"}
                </h4>
              </CardHeader>
              <CardBody>
                <p className="mb-0">
                  {t("languageInfo") ||
                    "Emails are sent in the user's preferred language. If a template is not available in the user's language, Spanish (ES) is used as fallback."}
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Edit Modal */}
      <Modal isOpen={editModalOpen} toggle={closeEditModal} size="xl">
        <ModalHeader toggle={closeEditModal}>
          {t("editTemplate")}: {t(`triggers.${selectedTrigger}`)}
        </ModalHeader>
        <ModalBody>
          {/* Language Tabs */}
          <Nav tabs className="mb-4">
            <NavItem>
              <NavLink
                className={classnames({ active: activeLanguage === "es" })}
                onClick={() => handleLanguageChange("es")}
                style={{ cursor: "pointer" }}
              >
                <span className={"flag-icon flag-icon-es mr-2"}></span>
                {t("languages.es")}
                {hasUnsavedChanges.es && (
                  <Badge color="warning" className="ml-2">
                    *
                  </Badge>
                )}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeLanguage === "en" })}
                onClick={() => handleLanguageChange("en")}
                style={{ cursor: "pointer" }}
              >
                <span className={"flag-icon flag-icon-gb mr-2"}></span>
                {t("languages.en")}
                {hasUnsavedChanges.en && (
                  <Badge color="warning" className="ml-2">
                    *
                  </Badge>
                )}
              </NavLink>
            </NavItem>
          </Nav>

          {!currentTemplate ? (
            <Alert color="warning">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              {t("noTemplateFor", { language: t(`languages.${activeLanguage}`) })}
            </Alert>
          ) : (
            <Row>
              {/* Form Column */}
              <Col md={8}>
                <Form>
                  <FormGroup>
                    <Label for="email-templates-field-1" className="form-control-label">{t("subject") || "Subject"} *</Label>
                    <Input id="email-templates-field-1"
                      type="text"
                      name="subject"
                      value={currentForm.subject}
                      onChange={handleFormChange}
                      placeholder={t("placeholders.subject")}
                    />
                  </FormGroup>

                  <Row>
                    <Col md={8}>
                      <FormGroup>
                        <Label for="email-templates-field-2" className="form-control-label">{t("titleField") || "Title"} *</Label>
                        <Input id="email-templates-field-2"
                          type="text"
                          name="title"
                          value={currentForm.title}
                          onChange={handleFormChange}
                          placeholder={t("placeholders.emailTitle")}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label for="email-templates-field-3" className="form-control-label">{t("titleColor") || "Title Color"}</Label>
                        <Input id="email-templates-field-3"
                          type="select"
                          name="titleColor"
                          value={currentForm.titleColor}
                          onChange={handleFormChange}
                        >
                          {TITLE_COLORS.map((color) => (
                            <option key={color.value} value={color.value}>
                              {t(`titleColors.${color.value}`)}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label for="email-templates-field-4" className="form-control-label">{t("greeting") || "Greeting"}</Label>
                    <Input id="email-templates-field-4"
                      type="text"
                      name="greeting"
                      value={currentForm.greeting}
                      onChange={handleFormChange}
                      placeholder={t("placeholders.greeting")}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label for="email-templates-field-5" className="form-control-label d-flex justify-content-between align-items-center">
                      <span>{t("bodyParagraphs") || "Body Paragraphs"} *</span>
                      <Button color="link" size="sm" onClick={addParagraph}>
                        <i className="fas fa-plus mr-1"></i>
                        {t("addParagraph") || "Add Paragraph"}
                      </Button>
                    </Label>
                    {currentForm.bodyParagraphs.map((paragraph, index) => (
                      <div key={index} className="d-flex mb-2">
                        <Input
                          type="textarea"
                          rows={2}
                          value={paragraph}
                          onChange={(e) => handleParagraphChange(index, e.target.value)}
                          placeholder={`Paragraph ${index + 1}...`}
                        />
                        {currentForm.bodyParagraphs.length > 1 && (
                          <Button color="link" className="text-danger ml-2" onClick={() => removeParagraph(index)}>
                            <i className="fas fa-times"></i>
                          </Button>
                        )}
                      </div>
                    ))}
                  </FormGroup>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label className="form-control-label">{t("buttonText") || "Button Text"}</Label>
                        <Input id="email-templates-field-5"
                          type="text"
                          name="buttonText"
                          value={currentForm.buttonText}
                          onChange={handleFormChange}
                          placeholder={t("placeholders.ctaText")}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="email-templates-field-6" className="form-control-label">{t("buttonUrl") || "Button URL"}</Label>
                        <Input id="email-templates-field-6"
                          type="text"
                          name="buttonUrl"
                          value={currentForm.buttonUrl}
                          onChange={handleFormChange}
                          placeholder="e.g., {{dashboardUrl}}"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label for="email-templates-field-7" className="form-control-label">{t("closingText") || "Closing Text"}</Label>
                    <Input id="email-templates-field-7"
                      type="textarea"
                      rows={2}
                      name="closingText"
                      value={currentForm.closingText}
                      onChange={handleFormChange}
                      placeholder={t("placeholders.signature")}
                    />
                  </FormGroup>
                </Form>
              </Col>

              {/* Variables Sidebar */}
              <Col md={4}>
                <Card className="bg-light">
                  <CardHeader className="bg-transparent">
                    <h5 className="mb-0">
                      <i className="fas fa-code mr-2"></i>
                      {t("availableVariables") || "Available Variables"}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    {triggerVariables.length > 0 ? (
                      <div>
                        <p className="small text-muted mb-3">
                          {t("variablesHelp") || "Click to copy. Use these placeholders in your template:"}
                        </p>
                        {triggerVariables.map((variable) => (
                          <Badge
                            key={variable}
                            color="secondary"
                            className="mr-2 mb-2"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              navigator.clipboard.writeText(`{{${variable}}}`);
                              notify.success(`Copied: {{${variable}}}`);
                            }}
                          >
                            {"{{" + variable + "}}"}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted mb-0">{t("noVariables") || "No variables available"}</p>
                    )}
                  </CardBody>
                </Card>

                <Card className="mt-3">
                  <CardHeader>
                    <h5 className="mb-0">
                      <i className="fas fa-palette mr-2"></i>
                      {t("colorPreview") || "Title Color Preview"}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <div
                      style={{
                        padding: "10px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "4px",
                      }}
                    >
                      <span
                        style={{
                          color: TITLE_COLORS.find((c) => c.value === currentForm.titleColor)?.color,
                          fontWeight: "bold",
                          fontSize: "1.1em",
                        }}
                      >
                        {currentForm.title || "Title Preview"}
                      </span>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="info" onClick={handlePreview} disabled={isLoadingPreview || !currentTemplate}>
            {isLoadingPreview ? (
              <>
                <Spinner size="sm" className="mr-2" />
                {t("loadingPreview") || "Loading..."}
              </>
            ) : (
              <>
                <i className="fas fa-eye mr-2"></i>
                {t("preview") || "Preview"}
              </>
            )}
          </Button>
          <Button color="warning" onClick={openTestModal} disabled={!currentTemplate}>
            <i className="fas fa-paper-plane mr-2"></i>
            {t("sendTest") || "Send Test"}
          </Button>
          <Button color="secondary" onClick={closeEditModal}>
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            color="primary"
            onClick={handleSave}
            disabled={isSaving || (!hasUnsavedChanges.es && !hasUnsavedChanges.en)}
          >
            {isSaving ? (
              <>
                <Spinner size="sm" className="mr-2" />
                {t("saving") || "Saving..."}
              </>
            ) : (
              <>
                <i className="fas fa-save mr-2"></i>
                {t("save") || "Save"} {(hasUnsavedChanges.es || hasUnsavedChanges.en) && "*"}
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Preview Modal */}
      <Modal isOpen={previewModalOpen} toggle={() => setPreviewModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setPreviewModalOpen(false)}>
          {t("emailPreview") || "Email Preview"} ({activeLanguage.toUpperCase()})
        </ModalHeader>
        <ModalBody style={{ padding: 0 }}>
          <iframe
            srcDoc={previewHtml}
            style={{
              width: "100%",
              height: "600px",
              border: "none",
            }}
            title="Email Preview"
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPreviewModalOpen(false)}>
            {t("close") || "Close"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Test Email Modal */}
      <Modal isOpen={testModalOpen} toggle={() => setTestModalOpen(false)}>
        <ModalHeader toggle={() => setTestModalOpen(false)}>
          {t("sendTestEmail") || "Send Test Email"} ({activeLanguage.toUpperCase()})
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="email-templates-field-8" className="form-control-label">{t("recipientEmail") || "Recipient Email"}</Label>
            <Input id="email-templates-field-8"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="email@example.com"
            />
            <small className="text-muted">
              {t("testEmailHelp") || "A test email will be sent using sample data for variables."}
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setTestModalOpen(false)}>
            {t("cancel") || "Cancel"}
          </Button>
          <Button color="primary" onClick={handleSendTest} disabled={isSendingTest || !testEmail}>
            {isSendingTest ? (
              <>
                <Spinner size="sm" className="mr-2" />
                {t("sending") || "Sending..."}
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane mr-2"></i>
                {t("send") || "Send"}
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default EmailTemplates;
